// 189 -- Add a child from scratch (Path B). For parents whose school has not
// issued a family code. Creates a Player + ParentPlayer link with NO school
// association; a coach establishes that by approving a sport signup (188).

import { useState } from "react";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export function AddChildDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (playerId: string) => void;
}) {
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [dob, setDob] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!firstName.trim() || !surname.trim() || !dob) {
      setError("First name, surname and date of birth are required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await api.createChild({
        first_name: firstName.trim(),
        surname: surname.trim(),
        date_of_birth: dob,
      });
      onCreated(res.player_id);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to add child");
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 p-4 sm:items-center"
      data-dialog="add-child"
    >
      <Card className="max-h-[90vh] w-full max-w-lg overflow-y-auto">
        <CardHeader>
          <CardTitle>Add a child</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Add your child here if your school has not given you a family code. When you sign them
            up for a sport, the coach confirms them onto the team.
          </p>
          {error && <p className="text-sm text-destructive" data-error>{error}</p>}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ac-first">First name</Label>
              <Input id="ac-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-field="first-name" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ac-surname">Surname</Label>
              <Input id="ac-surname" value={surname} onChange={(e) => setSurname(e.target.value)} data-field="surname" />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ac-dob">Date of birth</Label>
            <Input id="ac-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-field="dob" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving} data-action="create-child">
              {saving ? "Adding..." : "Add child"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
